import { AppLayout } from '@/components/layout/AppLayout'
import { AtlasPage } from '@/components/ui/AtlasPage'
import { PageTitle } from '@/components/ui/PageTitle'
import { Separator } from '@/components/ui/Separator'
import { PageNumber } from '@/components/ui/PageNumber'
import { Button } from '@/components/ui/Button'
import { Link, router } from '@inertiajs/react'
import { useConfirm } from '@/hooks/useConfirm'
import { ConfirmModal } from '@/components/ui/ConfirmModal'
import { CARE_ACTION_ICONS, EnumIcon } from '@/lib/icons'

interface CareTask {
  id: number
  plant_id: number
  task_type: string
  task_type_pl: string
  frequency_days: number | null
  instructions: string | null
  is_active: boolean
  next_due_at: string | null
  last_done_at: string | null
  overdue: boolean
  due_today: boolean
}

interface Props {
  plant: { id: number; name: string }
  care_tasks: CareTask[]
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('pl-PL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function CareTasksIndex({ plant, care_tasks }: Props) {
  const activeTasks = care_tasks.filter((t) => t.is_active)
  const inactiveTasks = care_tasks.filter((t) => !t.is_active)
  const { pending, confirm, handleConfirm, handleCancel } = useConfirm()

  const handleComplete = (taskId: number) => {
    router.post(`/care_tasks/${taskId}/complete`)
  }

  const handleDelete = (taskId: number) => {
    confirm('Na pewno usunąć to zadanie?', () => router.delete(`/care_tasks/${taskId}`))
  }

  return (
    <AtlasPage>
      <PageTitle
        title="Pielęgnacja"
        subtitle={`Harmonogram — ${plant.name}`}
      />
      <Separator />

      {/* Active tasks */}
      {activeTasks.length > 0 ? (
        <div className="space-y-sm">
          {activeTasks.map((task) => (
            <div
              key={task.id}
              className={`border p-sm ${
                task.overdue
                  ? 'border-danger/50 bg-danger/5'
                  : task.due_today
                    ? 'border-gold/50 bg-gold/5'
                    : 'border-frame/30'
              }`}
            >
              <div className="flex items-start justify-between gap-sm">
                <div className="flex-1">
                  <p className="font-label text-sm inline-flex items-center flex-wrap">
                    <EnumIcon entry={CARE_ACTION_ICONS[task.task_type]} size={14} className={task.overdue ? 'text-danger' : CARE_ACTION_ICONS[task.task_type]?.color} />
                    <span className={`ml-xs ${task.overdue ? 'text-danger font-semibold' : 'text-ink'}`}>
                      {task.task_type_pl}
                    </span>
                    {task.frequency_days && (
                      <span className="text-frame text-xs ml-sm">co {task.frequency_days} dni</span>
                    )}
                  </p>
                  {task.instructions && (
                    <p className="text-xs text-ink-light italic mt-xs">{task.instructions}</p>
                  )}
                  <div className="flex gap-md mt-xs text-xs text-frame">
                    {task.next_due_at && (
                      <span>
                        {task.overdue ? 'Zaległe od: ' : 'Następne: '}
                        {formatDate(task.next_due_at)}
                      </span>
                    )}
                    {task.last_done_at && (
                      <span>Ostatnio: {formatDate(task.last_done_at)}</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-xs shrink-0">
                  <Button size="sm" onClick={() => handleComplete(task.id)}>
                    Wykonaj
                  </Button>
                  <Button size="sm" variant="secondary" href={`/care_tasks/${task.id}/edit`}>
                    Edytuj
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => handleDelete(task.id)}>
                    Usuń
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-md">
          <p className="text-frame italic font-label">Brak aktywnych zadań.</p>
        </div>
      )}

      {/* Inactive tasks */}
      {inactiveTasks.length > 0 && (
        <>
          <Separator />
          <h2 className="font-display text-sm text-center uppercase tracking-wider text-frame mb-sm">
            Nieaktywne
          </h2>
          <div className="space-y-sm opacity-60">
            {inactiveTasks.map((task) => (
              <div key={task.id} className="border border-frame/20 p-sm">
                <div className="flex items-center justify-between">
                  <p className="font-label text-sm text-frame inline-flex items-center">
                    <EnumIcon entry={CARE_ACTION_ICONS[task.task_type]} size={14} className="text-frame" />
                    <span className="ml-xs">{task.task_type_pl}</span>
                    {task.frequency_days && (
                      <span className="text-xs ml-sm">co {task.frequency_days} dni</span>
                    )}
                  </p>
                  <div className="flex gap-xs">
                    <Button size="sm" variant="secondary" href={`/care_tasks/${task.id}/edit`}>
                      Edytuj
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => handleDelete(task.id)}>
                      Usuń
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <Separator />
      <div className="flex items-center justify-center gap-md">
        <Button href={`/plants/${plant.id}/care_tasks/new`}>Dodaj zadanie</Button>
        <Button href={`/plants/${plant.id}`} variant="secondary">Powrót do rośliny</Button>
      </div>

      <PageNumber number={14} />
      {pending && <ConfirmModal message={pending.message} onConfirm={handleConfirm} onCancel={handleCancel} />}
    </AtlasPage>
  )
}

CareTasksIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
export default CareTasksIndex
